<template>
  <div class="invoice-application-check">
    <el-dialog
      :model-value="visible"
      align-center
      :before-close="() => {
        emit('close');
      }"
      width="548"
    >
      <template #header>
        <h4>
          完税开票申请
        </h4>
      </template>

      <div v-loading="loading">
        <div v-if="!isHnOfflineInvoice" class="title">
          <img src="@core/main/assets/images/invoice-application.png" alt="">

          <div v-if="!isNullFreeTaxOrderAmount" class="item">
            <span>{{ `子订单汇总免税金额` }}</span>
            <span class="text-[#000000]">{{ isNull(info.freeTaxOrderAmount) ? '--' : `${info.freeTaxOrderAmount}元` }}</span>
          </div>
          <div v-if="!isNullStandardOrderAmount" class="item">
            <span>{{ `子订单汇总含税金额` }}</span>
            <span class="text-[#000000]">{{ isNull(info.standardOrderAmount) ? '--' : `${info.standardOrderAmount}元` }}</span>
          </div>
          <div class="item">
            <span>{{ `订单开票科目` }}</span>
            <span class="text-[#000000]">{{ isNull(info.invoiceSubNames) ? '--' : info.invoiceSubNames }}</span>
          </div>
        </div>

        <div v-if="checkInfo?.orderExceedCount" class="tips">
          本次申请中存在{{ checkInfo?.orderExceedCount || 0 }}笔单月派发超过{{ checkInfo?.distributeExceedAmount || 0 }}万的子订单
        </div>

        <el-form
          ref="formRef"
          require-asterisk-position="right"
          class="add-platform-users-form"
          :model="form"
          :rules="formRules"
        >
          <el-form-item
            label="完税开票科目"
            prop="invoiceSubId"
            class="mb-0"
          >
            <el-select v-model="form.invoiceSubId" filterable clearable placeholder="请选择完税开票科目">
              <el-option
                v-for="item in invoiceSubjectList"
                :key="item.id"
                :label="item.invoiceSubNameStr"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-form>
      </div>

      <template #footer>
        <div class="flex justify-end">
          <el-button @click="emit('close')">
            取消
          </el-button>
          <template v-if="!isHnOfflineInvoice">
            <el-button v-if="!isNullFreeTaxOrderAmount && !isNullStandardOrderAmount" :loading="saveLoading" @click="handleApplication('all')">
              同时申请
            </el-button>
            <el-button 
              v-if="!isNullFreeTaxOrderAmount"
              :loading="saveLoading" 
              type="primary"
              @click="handleApplication(ETaxInvoiceApplyStatus.FREE_TAX)"
            >
              免税订单开票申请
            </el-button>
            <el-button 
              v-if="!isNullStandardOrderAmount" 
              :loading="saveLoading"
              type="primary" 
              @click="handleApplication(ETaxInvoiceApplyStatus.STANDARD)"
            >
              含税订单开票申请
            </el-button>
          </template>
          <el-button 
            v-else
            :loading="saveLoading"
            type="primary" 
            @click="handleApplication(ETaxInvoiceApplyStatus.STANDARD)"
          >
            确认申请
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { queryHainanInvoiceList, queryInvoiceOfflineCheck, querySubmitCrowdApplication, queryTaxOrderAmount, submitInvoiceApplication } from '@core/main/api';
import { ElMessage, FormInstance, FormRules } from 'element-plus';
import { ICheckInoviceRes, IComputeFreeTaxOrderAmount } from '@core/main/types/crowd-source/invoice-pay-taxes';
import { computed, PropType, ref, watch } from 'vue';
import { isNull } from '@core/tools';
import { ETaxInvoiceApplyStatus, IHainanInvoiceItem } from '@core/main/types';

const emit = defineEmits(['close', 'confirm']);

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  serverId: {
    type: Number,
    default: undefined,
  },
  selectedIds: {
    type: Set as PropType<Set<number>>,
    default: () => new Set(),
  },
  isHnOfflineInvoice: {
    type: Boolean,
    default: false,
  },
});

const loading = ref(false);
const saveLoading = ref(false);
const info = ref<IComputeFreeTaxOrderAmount>({});
const form = ref({} as {invoiceSubId: number});
const formRef = ref<FormInstance>();
const checkInfo = ref<ICheckInoviceRes>();
const invoiceSubjectList = ref<IHainanInvoiceItem[]>([]);
const formRules = ref<FormRules<{invoiceSubId: number}>>({
  invoiceSubId: [
    {
      required: true,
      message: '请选择完税开票科目',

    },
  ],
});

const isNullFreeTaxOrderAmount = computed(() => info.value && info.value?.freeTaxOrderAmount === 0);
const isNullStandardOrderAmount = computed(() => info.value && info.value?.standardOrderAmount === 0);

async function fetchData() {
  try {
    info.value = await queryTaxOrderAmount(props.serverId, Array.from(props.selectedIds)) || {};
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function fetchInvoiceSubjectList() {
  try {
    invoiceSubjectList.value = await queryHainanInvoiceList(1, false);
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function handleAllApplication() {
  try {
    saveLoading.value = true;

    await querySubmitCrowdApplication(props?.serverId, form.value.invoiceSubId, Array.from(props.selectedIds));

    ElMessage.success('开票申请成功');
    emit('confirm');
    emit('close');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  } 
  saveLoading.value = false;
}

function handleApplication(invoiceType) {
  formRef.value?.validate((valid) => {
    if (valid) {
      if (invoiceType === 'all') {
        handleAllApplication();
      } else {
        doSubmit(invoiceType);
      }
    }
  });
}

async function doSubmit(invoiceType: ETaxInvoiceApplyStatus) {
  try {
    saveLoading.value = true;

    await submitInvoiceApplication({
      invoiceSubjectId: form.value.invoiceSubId,
      serverId: props?.serverId,
      invoiceType,
      orderDetailIds: Array.from(props.selectedIds),
    }, true);

    ElMessage.success(`${invoiceType === ETaxInvoiceApplyStatus.STANDARD ? '含税' : '免税'}开票申请成功`);
    emit('confirm');
    emit('close');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  } 
  saveLoading.value = false;
}

async function fetchCheckDistributionLimit() {
  loading.value = true;
  try {
    checkInfo.value = await queryInvoiceOfflineCheck(Array.from(props.selectedIds));
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
  loading.value = false;
}

watch(
  () => props.visible,
  () => {
    if (props.visible) {
      formRef.value?.resetFields();

      fetchInvoiceSubjectList();
      if (props.isHnOfflineInvoice) {
        fetchCheckDistributionLimit();
      } else {
        fetchData();
      }
    }
  },
  { immediate: true },
);
</script>

<style scoped lang="scss">
.invoice-application-check {
  :deep(.el-dialog) {
    padding: 0;

    .el-dialog__header {
      padding: 16px 24px 8px;

      h4 {
        color: #344767;
        font-size: 20px;
        font-weight: 600;
      }

      .el-dialog__headerbtn {
        top: 5px;
      }
    }

    .el-dialog__body {
      padding: 24px;

      .title {
        color: #8c8c8c;

        img {
          @apply h-[80px] mb-[23px];
        }
      }

      .item {
        @apply flex items-center gap-x-4 mb-3;

        span:nth-of-type(1) {
          @apply w-[160px] text-right text-[var(--color-text-desc)];
        }

        span:nth-of-type(2) {
          @apply text-[var(--color-text-primary)] w-[421px] truncate;
        }
      }

      .tips {
        @apply mb-4 text-[12px] rounded-[2px] text-[var(--color-danger)] px-[12px] py-[6px] flex items-center gap-x-2 bg-[var(--el-color-danger-light-9)] border-[var(--el-color-danger-light-8)] hover:border-[var(--el-color-danger-light-10)];
      }
    }

    .el-dialog__footer {
      padding: 8px 24px 16px;

      .el-button+.el-button {
        margin-left: 8px;
      }
    }
  }

  :deep(.el-form-item__label) {
    @apply w-[130px] mr-3;
  }
}
</style>
